import { motion } from "framer-motion";

/**
 * SpecialFX — renders the unique signature visual for a given `animationKey`.
 * All variants complete within 2–3 seconds so the show keeps its pace.
 *
 * The parent (CurrentNumberHero) is responsible for:
 *   - Keying this component with the number value so it remounts per reveal
 *   - Playing the matching sound
 *   - Optionally showing the nickname tagline
 */
export default function SpecialFX({ animationKey }) {
  switch (animationKey) {
    case "neon-pulse":
      return <NeonPulse />;
    case "shadow-thirteen":
      return <ShadowThirteen />;
    case "gold-key":
      return <GoldKey />;
    case "twin-ducks":
      return <TwinDucks />;
    case "halfway-pulse":
      return <HalfwayPulse />;
    case "mirror-flip":
      return <MirrorFlip />;
    case "sunset-strip":
      return <SunsetStrip />;
    case "double-sparkle-bloom":
      return <DoubleSparkleBloom />;
    case "grand-finale":
      return <GrandFinale />;
    default:
      return null;
  }
}

/* ============================================================
 * Helper: centered absolute wrapper
 * ============================================================ */
const Center = ({ children }) => (
  <div
    aria-hidden
    className="absolute inset-0 pointer-events-none flex items-center justify-center"
  >
    {children}
  </div>
);

/* ============================================================
 * 7 — Neon Pulse
 * Three rapid electric flashes, persistent afterglow, primary color
 * ============================================================ */
function NeonPulse() {
  const flashes = [0, 0.14, 0.28];
  return (
    <Center>
      {/* Rapid flash strobe */}
      {flashes.map((delay, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: [0.6, 1.6, 0.6], opacity: [0, 1, 0] }}
          transition={{ duration: 0.5, delay, ease: "easeOut" }}
          className="absolute rounded-full"
          style={{
            width: 520,
            height: 520,
            background:
              "radial-gradient(circle, var(--ic-primary-glow) 0%, transparent 55%)",
            filter: "blur(4px)",
          }}
        />
      ))}
      {/* Trailing afterglow */}
      <motion.span
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 3.2, opacity: [0, 0.6, 0] }}
        transition={{ duration: 1.6, delay: 0.5, ease: "easeOut" }}
        className="absolute rounded-full"
        style={{
          width: 420,
          height: 420,
          background:
            "radial-gradient(circle, var(--ic-primary-glow) 0%, transparent 70%)",
          filter: "blur(24px)",
        }}
      />
    </Center>
  );
}

/* ============================================================
 * 13 — Shadow Thirteen
 * Dark ripple emanating outward, red-tinted edge
 * ============================================================ */
function ShadowThirteen() {
  return (
    <Center>
      {[0, 0.25, 0.5].map((d, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0.3, opacity: 0.9 }}
          animate={{ scale: 4.4, opacity: 0 }}
          transition={{ duration: 1.6, delay: d, ease: "easeOut" }}
          className="absolute rounded-full"
          style={{
            width: 240,
            height: 240,
            border: "3px solid rgba(239, 68, 68, 0.7)",
            boxShadow:
              "inset 0 0 40px rgba(0,0,0,0.9), 0 0 30px rgba(239,68,68,0.4)",
            filter: "blur(2px)",
          }}
        />
      ))}
    </Center>
  );
}

/* ============================================================
 * 21 — Gold Key
 * Slow radiant golden light pulse + rising particles
 * ============================================================ */
function GoldKey() {
  const rays = Array.from({ length: 12 });
  return (
    <Center>
      {/* Central sun */}
      <motion.span
        initial={{ scale: 0.2, opacity: 0.9 }}
        animate={{ scale: 2, opacity: 0 }}
        transition={{ duration: 2.2, ease: "easeOut" }}
        className="absolute rounded-full"
        style={{
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(250,204,21,0.9) 0%, rgba(245,158,11,0.6) 40%, transparent 75%)",
          filter: "blur(8px)",
        }}
      />
      {/* Rays */}
      {rays.map((_, i) => {
        const rot = (i / rays.length) * 360;
        return (
          <motion.span
            key={i}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: [0, 1.6, 0], opacity: [0, 0.8, 0] }}
            transition={{
              duration: 1.4,
              delay: 0.2 + (i % 4) * 0.05,
              ease: "easeOut",
            }}
            className="absolute origin-center"
            style={{
              width: 4,
              height: 260,
              background:
                "linear-gradient(to top, transparent, rgba(250,204,21,0.9), transparent)",
              transform: `rotate(${rot}deg)`,
            }}
          />
        );
      })}
    </Center>
  );
}

/* ============================================================
 * 22 — Two Little Ducks
 * Two side-by-side glow pulses that merge, teal accent
 * ============================================================ */
function TwinDucks() {
  return (
    <Center>
      {[-160, 160].map((x, i) => (
        <motion.span
          key={i}
          initial={{ x, scale: 0.3, opacity: 0.9 }}
          animate={{ x: 0, scale: [0.3, 1.4, 0.9], opacity: [0.9, 1, 0] }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          className="absolute rounded-full"
          style={{
            width: 300,
            height: 300,
            background:
              "radial-gradient(circle, rgba(20, 184, 166, 0.7) 0%, rgba(6, 148, 162, 0.4) 40%, transparent 70%)",
            filter: "blur(12px)",
          }}
        />
      ))}
      {/* Final merged glow */}
      <motion.span
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ scale: 2.4, opacity: [0, 0.7, 0] }}
        transition={{ duration: 1.4, delay: 0.7, ease: "easeOut" }}
        className="absolute rounded-full"
        style={{
          width: 260,
          height: 260,
          background:
            "radial-gradient(circle, rgba(20,184,166,0.7) 0%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />
    </Center>
  );
}

/* ============================================================
 * 45 — Halfway Pulse
 * Horizontal sweep line + radial pulse
 * ============================================================ */
function HalfwayPulse() {
  return (
    <Center>
      <motion.span
        initial={{ scaleX: 0, opacity: 0.9 }}
        animate={{ scaleX: 1, opacity: [0.9, 1, 0] }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="absolute h-1"
        style={{
          width: "80vw",
          background:
            "linear-gradient(to right, transparent, var(--ic-primary), transparent)",
          boxShadow: "0 0 30px var(--ic-primary-glow)",
        }}
      />
      {[0, 0.25].map((d, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0.3, opacity: 0.9 }}
          animate={{ scale: 3.4, opacity: 0 }}
          transition={{ duration: 1.6, delay: d, ease: "easeOut" }}
          className="absolute rounded-full"
          style={{
            width: 260,
            height: 260,
            border: "3px solid var(--ic-primary)",
            filter: "blur(2px)",
          }}
        />
      ))}
    </Center>
  );
}

/* ============================================================
 * 69 — Mirror Flip
 * Two sparkle streams from opposite corners meet at center
 * ============================================================ */
function MirrorFlip() {
  return (
    <Center>
      {[
        { x: -600, y: -300 },
        { x: 600, y: 300 },
      ].map((from, i) => (
        <motion.span
          key={i}
          initial={{ x: from.x, y: from.y, opacity: 1, scale: 0.6 }}
          animate={{ x: 0, y: 0, opacity: [1, 1, 0], scale: [0.6, 1.4, 0.4] }}
          transition={{ duration: 1.8, ease: "easeInOut" }}
          className="absolute rounded-full"
          style={{
            width: 60,
            height: 60,
            background:
              "radial-gradient(circle, rgba(168, 85, 247, 0.9) 0%, rgba(139, 92, 246, 0.5) 40%, transparent 75%)",
            boxShadow: "0 0 30px rgba(168, 85, 247, 0.8)",
            filter: "blur(1px)",
          }}
        />
      ))}
      <motion.span
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 2.6, opacity: [0, 0.9, 0] }}
        transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
        className="absolute rounded-full"
        style={{
          width: 300,
          height: 300,
          background:
            "radial-gradient(circle, rgba(168,85,247,0.6) 0%, transparent 70%)",
          filter: "blur(18px)",
        }}
      />
    </Center>
  );
}

/* ============================================================
 * 77 — Sunset Strip
 * Warm gradient background + rising golden particles
 * ============================================================ */
function SunsetStrip() {
  const rising = Array.from({ length: 16 });
  return (
    <Center>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.75, 0] }}
        transition={{ duration: 2.6, ease: "easeInOut" }}
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 30%, rgba(251,146,60,0.35) 60%, rgba(239,68,68,0.4) 100%)",
        }}
      />
      {rising.map((_, i) => {
        const left = (i * 61) % 100;
        const delay = (i * 0.09) % 1;
        return (
          <motion.span
            key={i}
            initial={{ y: 200, opacity: 0 }}
            animate={{ y: -300, opacity: [0, 1, 0] }}
            transition={{ duration: 2.4, delay, ease: "easeOut" }}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              bottom: "10%",
              width: 4 + (i % 3),
              height: 4 + (i % 3),
              background: "rgba(251, 191, 36, 0.95)",
              boxShadow: "0 0 12px rgba(251, 191, 36, 0.9)",
            }}
          />
        );
      })}
    </Center>
  );
}

/* ============================================================
 * 88 — Double Sparkle Bloom
 * Two intersecting light rings expanding, slow rotation
 * ============================================================ */
function DoubleSparkleBloom() {
  const stars = Array.from({ length: 36 });
  return (
    <Center>
      {[0, 45].map((rot, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0.2, opacity: 0.9, rotate: rot }}
          animate={{ scale: 3.4, opacity: 0, rotate: rot + 40 }}
          transition={{ duration: 2.2, delay: i * 0.15, ease: "easeOut" }}
          className="absolute"
          style={{
            width: 360,
            height: 360,
            borderRadius: "50%",
            border: "3px solid var(--ic-primary)",
            boxShadow: "0 0 40px var(--ic-primary-glow)",
            filter: "blur(1px)",
          }}
        />
      ))}
      {stars.map((_, i) => {
        const a = (i / stars.length) * 360;
        const r = 260 + (i % 3) * 40;
        const rad = (a * Math.PI) / 180;
        return (
          <motion.span
            key={`s-${i}`}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
            animate={{
              x: Math.cos(rad) * r,
              y: Math.sin(rad) * r,
              opacity: 0,
              scale: 1.2,
            }}
            transition={{ duration: 2, delay: 0.2, ease: "easeOut" }}
            className="absolute rounded-full"
            style={{
              width: 6,
              height: 6,
              background: "#fff",
              boxShadow: "0 0 12px var(--ic-primary-glow)",
            }}
          />
        );
      })}
    </Center>
  );
}

/* ============================================================
 * 90 — Grand Finale
 * Screen shake + huge gold burst + confetti explosion
 * ============================================================ */
function GrandFinale() {
  const confetti = Array.from({ length: 60 });
  const colors = ["#fbbf24", "#f59e0b", "#fde68a", "#ffffff", "#f97316"];
  return (
    <>
      {/* Screen flash */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.35, 0] }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="absolute inset-0"
        style={{ background: "rgba(255, 220, 100, 1)" }}
      />
      <Center>
        {/* Huge gold burst */}
        <motion.span
          initial={{ scale: 0.1, opacity: 1 }}
          animate={{ scale: 6, opacity: 0 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute rounded-full"
          style={{
            width: 420,
            height: 420,
            background:
              "radial-gradient(circle, rgba(251,191,36,1) 0%, rgba(245,158,11,0.7) 30%, transparent 70%)",
            filter: "blur(10px)",
          }}
        />
        {/* Confetti explosion */}
        {confetti.map((_, i) => {
          const angle = (i / confetti.length) * 360 + Math.random() * 30;
          const dist = 320 + Math.random() * 380;
          const rad = (angle * Math.PI) / 180;
          const x = Math.cos(rad) * dist;
          const y = Math.sin(rad) * dist;
          const c = colors[i % colors.length];
          return (
            <motion.span
              key={i}
              initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.6 }}
              animate={{
                x,
                y: y + 200, // gravity drop
                opacity: [1, 1, 0],
                rotate: 720,
                scale: 1,
              }}
              transition={{ duration: 2.6, ease: "easeOut" }}
              className="absolute"
              style={{
                width: 8 + Math.random() * 8,
                height: 12 + Math.random() * 10,
                background: c,
                boxShadow: `0 0 12px ${c}`,
                borderRadius: 2,
              }}
            />
          );
        })}
      </Center>
    </>
  );
}
